/**
 * Services Section — Insolip brand (navy, service cards with royal/gold accents)
 */
import { motion } from 'motion/react';
import { Workflow, Shield, GraduationCap, ArrowRight } from 'lucide-react';
import { content } from '@/landing/data/content';

const iconMap = {
  Workflow,
  Shield,
  GraduationCap,
};

const serviceImages = [
  'https://images.unsplash.com/photo-1637856794303-d864ce316444?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&q=80&w=1080',
  'https://images.unsplash.com/photo-1752697589128-f8e110a86af3?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&q=80&w=1080',
  'https://images.unsplash.com/photo-1631599143419-ea8539ed4fbd?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&q=80&w=1080',
];

export function Services() {
  return (
    <section id="services" className="relative py-24 lg:py-32 overflow-hidden bg-[var(--navy)] border-t border-[rgba(201,168,76,0.15)]">
      <div className="absolute inset-0 hero-grid-overlay opacity-50" />
      <div className="container mx-auto px-4 lg:px-8 relative z-10">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="text-center mb-16"
        >
          <h2 className="font-display text-4xl md:text-5xl font-semibold text-[var(--gold)] mb-4">
            Our Services
          </h2>
          <p className="text-lg text-[var(--text-muted)] max-w-3xl mx-auto font-light">
            Comprehensive solutions for every stage of your innovation journey
          </p>
        </motion.div>

        <div className="grid md:grid-cols-3 gap-8 items-stretch">
          {content.services.map((service, index) => {
            const Icon = iconMap[service.icon as keyof typeof iconMap];
            return (
              <motion.div
                key={service.title}
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.15, duration: 0.8 }}
                className="group relative flex flex-col rounded-2xl overflow-hidden border border-[rgba(255,255,255,0.08)] hover:border-[rgba(201,168,76,0.3)] transition-all duration-500 h-full"
              >
                {/* Top accent bar on hover */}
                <div className="absolute top-0 left-0 right-0 h-1 bg-transparent group-hover:bg-gradient-to-r group-hover:from-[var(--royal)] group-hover:to-[var(--gold)] transition-all duration-300 z-10" />
                <div className="relative h-64 flex-shrink-0 overflow-hidden">
                  <motion.img
                    src={serviceImages[index]}
                    alt={service.title}
                    className="w-full h-full object-cover opacity-80"
                    whileHover={{ scale: 1.05 }}
                    transition={{ duration: 0.6 }}
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-[var(--navy)]/80 via-transparent to-transparent" />
                  <motion.div
                    initial={{ scale: 0 }}
                    whileInView={{ scale: 1 }}
                    viewport={{ once: true }}
                    transition={{ delay: index * 0.15 + 0.4, type: 'spring', stiffness: 200 }}
                    className="absolute top-6 right-6 w-14 h-14 rounded-2xl bg-[var(--navy)]/90 backdrop-blur-sm border border-[rgba(201,168,76,0.2)] flex items-center justify-center"
                  >
                    <Icon className="w-7 h-7 text-[var(--gold)]" />
                  </motion.div>
                </div>
                <div className="insolip-card-gradient flex-1 flex flex-col p-8 min-h-[200px]">
                  <h3 className="font-display text-2xl font-semibold text-white mb-4 group-hover:text-[var(--gold-light)] transition-colors duration-300">
                    {service.title}
                  </h3>
                  <p className="text-white/85 leading-relaxed mb-6 font-light flex-1">
                    {service.description}
                  </p>
                  <motion.a
                    href="#contact"
                    className="inline-flex items-center gap-2 text-[var(--gold)] font-semibold group-hover:gap-4 transition-all duration-300 flex-shrink-0"
                    whileHover={{ x: 5 }}
                  >
                    Learn More
                    <ArrowRight className="w-5 h-5" />
                  </motion.a>
                </div>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
